/**
 * Loop guard for the automatic sign-in redirect. The web apps send the browser
 * straight to the themed sign-in page; if the browser keeps coming back signed
 * out (a misconfigured client, a refused callback), the app stops redirecting
 * and shows the error state instead of bouncing forever.
 */
export const REDIRECT_KEY = '24k-signin-redirects';
export const MAX_REDIRECTS = 3;
export const WINDOW_MS = 60000;

function storage() {
  try { return window.sessionStorage; } catch (_) { return null; }
}

function recent(store, now) {
  try {
    const list = JSON.parse((store && store.getItem(REDIRECT_KEY)) || '[]');
    return Array.isArray(list) ? list.filter((t) => typeof t === 'number' && now - t < WINDOW_MS) : [];
  } catch (_) { return []; }
}

/** False once MAX_REDIRECTS redirects started within WINDOW_MS without a signed-in return. */
export function shouldAutoRedirect(now = Date.now(), store = storage()) {
  return recent(store, now).length < MAX_REDIRECTS;
}

export function noteRedirect(now = Date.now(), store = storage()) {
  if (!store) return;
  try { store.setItem(REDIRECT_KEY, JSON.stringify([...recent(store, now), now])); } catch (_) {}
}

/** Called after a signed-in return, or when the visitor asks to try again. */
export function resetRedirects(store = storage()) {
  try { if (store) store.removeItem(REDIRECT_KEY); } catch (_) {}
}

/** The realm's issuer from a keycloak-js config object or an adapter (keycloak.json) document. */
export function issuerFromConfig(config) {
  if (!config || typeof config !== 'object') return null;
  const base = String(config.url || config['auth-server-url'] || config.authServerUrl || '').replace(/\/+$/, '');
  const realm = config.realm ? encodeURIComponent(config.realm) : '';
  return base && realm ? `${base}/realms/${realm}` : null;
}

/**
 * Whether the identity server answers, before the app sends the whole page there (a full-page
 * redirect to a dead server leaves the visitor on the browser's own error page). A no-cors
 * request: any answer counts as reachable, only a network failure or the timeout does not.
 */
export async function identityServerReachable(issuer, { timeoutMs = 6000, fetchImpl = typeof fetch === 'function' ? fetch.bind(globalThis) : null } = {}) {
  if (!issuer || !fetchImpl) return true;
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    await fetchImpl(`${issuer}/.well-known/openid-configuration`, { mode: 'no-cors', cache: 'no-store', credentials: 'omit', signal: controller ? controller.signal : undefined });
    return true;
  } catch (_) {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * A return path without an identity-server response in its fragment
 * (#state=…&code=… or #error=login_required&state=… after check-sso). Kept in
 * returnTo, that fragment followed the visitor back after sign-in.
 */
export function withoutAuthResponse(path) {
  const text = String(path || '');
  const hash = text.indexOf('#');
  if (hash < 0) return text;
  const params = new URLSearchParams(text.slice(hash + 1));
  const isAuthResponse = params.has('state') && (params.has('code') || params.has('error'));
  return isAuthResponse ? text.slice(0, hash) : text;
}
